import { isAndroid, isIOS } from '@cdc-internal/fe-toolbox/utils/device'
import { AnalyticsBrowser } from '@segment/analytics-next'

import { logger } from '@/configs'
import { currentAppEnvConfigs } from '@/configs/env'

import { checkConsentForGroup, setupConsentListener, waitForOneTrust } from '../one-trust/utils'

// Locale pattern for removing locale prefixes from URLs
// Supports formats like: /en, /zh-TW, /en_US, /zh_CN, etc.
const LOCALE_PATTERN = /^\/[a-z]{2}(?:[-_][A-Z]{2})?/i

// Types for Segment events
export interface IdentifyTraits {
  email?: string
  name?: string
  username?: string
  phone?: string
  [key: string]: any
}

export interface PageProperties {
  title?: string
  url?: string
  referrer?: string
  [key: string]: any
}

/**
 * Waits for OneTrust consent before loading Segment
 * Recursively checks if OneTrust is available and calls consent check
 */
export function loadSegmentAfterConsent(): void {
  waitForOneTrust(() => {
    checkConsentAndLoad()
  })
}

/**
 * Checks OneTrust's initial consent state and loads Segment if consent is granted
 * Specifically looks for C0002 consent group (analytics consent)
 */
export function checkConsentAndLoad(): void {
  // Check for C0002 consent (analytics consent)
  const hasConsent = checkConsentForGroup('C0002')

  if (hasConsent) {
    loadSegment()
  } else {
    // Wait for a while and check again, as OneTrust might not be fully loaded yet
    setTimeout(() => {
      const retryConsent = checkConsentForGroup('C0002')
      if (retryConsent) {
        loadSegment()
      } else {
        logger.warn('Consent still not granted on retry')
      }
    }, 2000)
  }
}

/**
 * Sets up the OneTrust consent change listener
 * This should be called after the initial consent check
 */
export function setupOneTrustListener(): void {
  setupConsentListener(
    'C0002', // Analytics consent group
    () => {
      loadSegment()
    },
    () => {
      disableSegment()
    },
  )
}

/**
 * Initializes Segment with the provided segment ID
 * This is the main entry point that should be called from the HTML string
 */
export function initializeSegment(): void {
  try {
    loadSegmentAfterConsent()
    setupOneTrustListener()
  } catch (error) {
    logger.error('Error initializing Segment: %o', error)
  }
}

let analytics: AnalyticsBrowser | null = null

export function createAnalytics(writeKey: string): AnalyticsBrowser {
  if (!analytics) {
    analytics = new AnalyticsBrowser()

    analytics.load({ writeKey })

    // Mount analytics instance to window for compatibility
    ;(window as any).analytics = analytics
  }

  return analytics
}

/**
 * Get the current analytics instance
 * @returns AnalyticsBrowser instance or null if not initialized
 */
export function getAnalytics(): AnalyticsBrowser | null {
  return analytics
}

/**
 * Identify a user with Segment
 * @param userId Unique identifier for the user
 * @param traits User traits/properties
 * @param options Additional options
 */
export function identify(userId?: string, traits?: IdentifyTraits, options?: any): void {
  try {
    const analyticsInstance = getAnalytics()
    if (analyticsInstance) {
      const platform = getPlatform()
      const traitsWithPlatform = {
        ...traits,
        platform,
      }
      const identifyOptions = {
        ...options,
        context: {
          ...options?.context,
        },
      }
      analyticsInstance.identify(userId, traitsWithPlatform, identifyOptions)
    }
  } catch (error) {
    logger.error('Error identifying user: %o', error)
  }
}

/**
 * Get the event name based on URL pattern
 * @param url The URL to analyze
 * @returns The appropriate event name
 */
export function getEventNameFromUrl(url: string): string {
  // Check for /price/{slug} pattern -> Coin (regardless of locale)
  if (url.match(/\/price\/[^/]+$/)) {
    return 'Coin'
  }

  // Check for /price pattern -> Market (regardless of locale)
  if (url.match(/\/price$/)) {
    return 'Market'
  }

  // Remove locale prefix using the configurable pattern for other cases
  const pathWithoutLocale = url.replace(LOCALE_PATTERN, '')
  return pathWithoutLocale || '/'
}

const getPlatform = (): 'web' | 'web_ios' | 'web_android' => {
  if (typeof window === 'undefined') return 'web'
  if (isIOS) return 'web_ios'
  if (isAndroid) return 'web_android'
  return 'web'
}

/**
 * Track a page view with Segment
 * @param url The current URL (optional, will use window.location.pathname if not provided)
 * @param properties Page properties (optional)
 * @param options Additional options (optional)
 */
export function page(url?: string, properties?: PageProperties, options?: any): void {
  try {
    const analyticsInstance = getAnalytics()
    if (analyticsInstance) {
      // Get URL from parameter or current location
      const currentUrl = url || (typeof window !== 'undefined' ? window.location.href : '')
      const currentPath = typeof window !== 'undefined' ? window.location.pathname : ''

      // Determine event name based on URL pattern
      const eventName = getEventNameFromUrl(currentPath)

      // Create properties with URL information
      const pageProperties = {
        ...properties,
        url: currentUrl,
        path: currentPath,
      }

      const platform = getPlatform()
      const pagePropertiesWithPlatform = {
        ...pageProperties,
        platform,
      }
      const pageOptions = {
        ...options,
        context: {
          ...options?.context,
        },
      }

      analyticsInstance.page(eventName, pagePropertiesWithPlatform, pageOptions)
    }
  } catch (error) {
    logger.error('Error tracking page: %o', error)
  }
}

/**
 * Track a custom event with Segment
 * @param event Event name
 * @param properties Event properties (optional)
 * @param options Additional options (optional)
 */
export function track(event: string, properties?: any, options?: any): void {
  try {
    const analyticsInstance = getAnalytics()
    if (analyticsInstance) {
      const platform = getPlatform()
      const propertiesWithPlatform = {
        ...properties,
        platform,
      }
      const trackOptions = {
        ...options,
        context: {
          ...options?.context,
        },
      }
      analyticsInstance.track(event, propertiesWithPlatform, trackOptions)
    }
  } catch (error) {
    logger.error('Error tracking event: %o', error)
  }
}

/**
 * Disables Segment and cleans up resources
 * This should be called when consent is revoked
 */
export function disableSegment(): void {
  try {
    if (analytics) {
      // Clear any queued events
      analytics.reset()
      analytics = null

      // Clean up window.analytics
      delete (window as any).analytics
    }
  } catch (error) {
    logger.error('Error disabling Segment: %o', error)
  }
}

/**
 * Loads Segment with the stored segment ID and triggers initial page view
 * This should be called after consent is granted
 */
export function loadSegment(): void {
  const segmentId = currentAppEnvConfigs.segmentId

  if (!segmentId) return

  try {
    createAnalytics(segmentId)

    // Track initial page view with current URL
    const currentUrl = typeof window !== 'undefined' ? window.location.href : ''
    page(currentUrl)
    identify()
  } catch (error) {
    logger.error('Error loading Segment: %o', error)
  }
}
